import { api, apiUrl } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  proposalDetailDtoSchema,
  proposalDtoSchema,
  proposalListDtoSchema,
  proposalVersionDtoSchema,
  type ProposalDetailDto,
  type ProposalDto,
  type ProposalListDto,
  type ProposalVersionDto,
} from '@lib/dto/proposal.dto'
import {
  proposalDocumentDtoSchema,
  type ProposalDocumentDto,
} from '@lib/dto/proposal-document.dto'
import {
  proposalSelectionDtoSchema,
  type ProposalSelectionDto,
} from '@lib/dto/proposal-selection.dto'
import type { AcceptProposalInput } from '@lib/input/proposal-selection.input'
import {
  proposalMeetingRequestDtoSchema,
  type ProposalMeetingRequestDto,
} from '@lib/dto/proposal-meeting.dto'
import type { RequestMeetingInput } from '@lib/input/proposal-meeting.input'
import type { RecordActivityInput } from '@lib/input/proposal-activity.input'
import {
  proposalActivitySummaryDtoSchema,
  type ProposalActivitySummaryDto,
} from '@lib/dto/proposal-activity.dto'
import {
  proposalFollowUpDtoSchema,
  type ProposalFollowUpDto,
} from '@lib/dto/proposal-follow-up.dto'
import {
  proposalPdfDtoSchema,
  proposalVideoPlaybackDtoSchema,
  type ProposalPdfDto,
  type ProposalVideoPlaybackDto,
} from '@lib/dto/proposal-media.dto'
import { z } from 'zod'
import {
  proposalDeliveryDtoSchema,
  type ProposalDeliveryDto,
} from '@lib/dto/proposal-delivery.dto'
import type {
  CreateProposalInput,
  CreateProposalVersionInput,
  ListProposalsQuery,
  ProposalVersionDecisionInput,
  UpdateProposalInput,
  UpdateProposalVersionInput,
} from '@lib/input/proposal.input'
import type { IProposalService } from '@/types/ProposalService.types'
import { toQueryString } from '@/helpers/queryString'

/**
 * Service for proposals and their versions. Responses are validated against the
 * shared `@lib` schemas at the boundary.
 */
class ProposalService implements IProposalService {
  async list(
    query: Partial<ListProposalsQuery> = {},
  ): Promise<ProposalListDto> {
    try {
      return await api.get(
        `/proposals${toQueryString(query)}`,
        proposalListDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to list proposals', { error: toErrorMessage(error) })
      throw error
    }
  }

  async getDocument(versionId: string): Promise<ProposalDocumentDto> {
    try {
      return await api.get(
        `/proposal-versions/${encodeURIComponent(versionId)}/document`,
        proposalDocumentDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to load proposal document', {
        versionId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async get(id: string): Promise<ProposalDetailDto> {
    try {
      return await api.get(
        `/proposals/${encodeURIComponent(id)}`,
        proposalDetailDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to load proposal', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async create(input: CreateProposalInput): Promise<ProposalDetailDto> {
    try {
      return await api.post('/proposals', input, proposalDetailDtoSchema)
    } catch (error) {
      logger.error('Failed to create proposal', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async update(id: string, input: UpdateProposalInput): Promise<ProposalDto> {
    try {
      return await api.put(
        `/proposals/${encodeURIComponent(id)}`,
        input,
        proposalDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to update proposal', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async setArchived(id: string, archived: boolean): Promise<ProposalDto> {
    const action = archived ? 'archive' : 'restore'
    try {
      return await api.post(
        `/proposals/${encodeURIComponent(id)}/${action}`,
        {},
        proposalDtoSchema,
      )
    } catch (error) {
      logger.error(`Failed to ${action} proposal`, {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async addVersion(
    proposalId: string,
    input: CreateProposalVersionInput,
  ): Promise<ProposalVersionDto> {
    try {
      return await api.post(
        `/proposals/${encodeURIComponent(proposalId)}/versions`,
        input,
        proposalVersionDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to add proposal version', {
        proposalId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async updateVersion(
    versionId: string,
    input: UpdateProposalVersionInput,
  ): Promise<ProposalVersionDto> {
    try {
      return await api.put(
        `/proposal-versions/${encodeURIComponent(versionId)}`,
        input,
        proposalVersionDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to update proposal version', {
        versionId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async switchToNewestVisits(versionId: string): Promise<ProposalVersionDto> {
    try {
      return await api.post(
        `/proposal-versions/${encodeURIComponent(versionId)}/use-newest-visits`,
        {},
        proposalVersionDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to rebuild a draft from the newest visits', {
        versionId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async send(versionId: string): Promise<ProposalVersionDto> {
    try {
      return await api.post(
        `/proposal-versions/${encodeURIComponent(versionId)}/send`,
        {},
        proposalVersionDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to send proposal version', {
        versionId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /**
   * Email a version to the client, and record the attempt.
   *
   * Distinct from `send`, which only marks a version sent by hand. Both
   * freeze the document; only this one delivers anything.
   */
  async deliver(versionId: string): Promise<ProposalDeliveryDto> {
    try {
      return await api.post(
        `/proposal-versions/${encodeURIComponent(versionId)}/deliveries`,
        {},
        proposalDeliveryDtoSchema,
      )
    } catch (error) {
      logger.error("Failed to email a proposal version", {
        versionId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /**
   * Ask for someone at the board meeting, from a shared link.
   *
   * No account, like accepting: the token in the URL is the authorization.
   */
  async requestMeetingShared(
    token: string,
    input: RequestMeetingInput,
  ): Promise<ProposalMeetingRequestDto> {
    try {
      return await api.post(
        `/shared-proposals/${encodeURIComponent(token)}/meeting-request`,
        input,
        proposalMeetingRequestDtoSchema,
      )
    } catch (error) {
      logger.error("Failed to request a board meeting", {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async listMeetingRequests(
    versionId: string,
  ): Promise<ProposalMeetingRequestDto[]> {
    try {
      return await api.get(
        `/proposal-versions/${encodeURIComponent(versionId)}/meeting-requests`,
        z.array(proposalMeetingRequestDtoSchema),
      )
    } catch (error) {
      logger.error("Failed to load board meeting requests", {
        versionId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /**
   * Record what the reader did on a shared proposal, in a small batch.
   *
   * `keepalive` so a batch sent as the tab is hidden or closed still arrives.
   * Rethrows like every other method; the caller decides whether a lost
   * batch matters (it does not much: these are signals, not records).
   */
  async recordActivityShared(
    token: string,
    input: RecordActivityInput,
  ): Promise<void> {
    try {
      await api.postNoContent(
        `/shared-proposals/${encodeURIComponent(token)}/activity`,
        input,
        { keepalive: true },
      )
    } catch (error) {
      logger.warn('Failed to record proposal activity', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /**
   * A short-lived playback link for one tree video on a shared proposal.
   *
   * Fetched when somebody presses play rather than with the document: Stream
   * links are signed and expire, and the route only serves videos on the
   * proposal the token opens.
   */
  async getSharedVideoPlayback(
    token: string,
    videoId: string,
  ): Promise<ProposalVideoPlaybackDto> {
    try {
      return await api.get(
        `/shared-proposals/${encodeURIComponent(token)}/videos/${encodeURIComponent(videoId)}`,
        proposalVideoPlaybackDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to load a proposal video', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /** What the client did on their shared link for one version. */
  async listActivity(versionId: string): Promise<ProposalActivitySummaryDto> {
    try {
      return await api.get(
        `/proposal-versions/${encodeURIComponent(versionId)}/activity`,
        proposalActivitySummaryDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to load proposal activity', {
        versionId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /** Accepted proposals with work left for later, newest acceptance first. */
  async listFollowUps(): Promise<ProposalFollowUpDto[]> {
    try {
      return await api.get(
        '/proposal-follow-ups',
        z.array(proposalFollowUpDtoSchema),
      )
    } catch (error) {
      logger.error('Failed to load proposal follow-ups', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /** The proposal behind a shared link. No account, no token header. */
  async getShared(token: string): Promise<ProposalDocumentDto> {
    try {
      return await api.get(
        `/shared-proposals/${encodeURIComponent(token)}`,
        proposalDocumentDtoSchema,
      )
    } catch (error) {
      logger.error("Failed to load a shared proposal", {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /**
   * Where to download a shared proposal as a PDF. The first request makes
   * it, which takes a few seconds. Fails with a 503 where PDFs are not set
   * up, and the page prints from the browser instead, so this warns rather
   * than errors.
   */
  async getSharedPdf(token: string): Promise<ProposalPdfDto> {
    try {
      return await api.get(
        `/shared-proposals/${encodeURIComponent(token)}/pdf`,
        proposalPdfDtoSchema,
      )
    } catch (error) {
      logger.warn('Could not get the PDF of a shared proposal', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /** Where to download a sent version as a PDF, without the client's QR code. */
  async getPdf(versionId: string): Promise<ProposalPdfDto> {
    try {
      return await api.get(
        `/proposal-versions/${encodeURIComponent(versionId)}/pdf`,
        proposalPdfDtoSchema,
      )
    } catch (error) {
      logger.warn('Could not get the PDF of a proposal version', {
        versionId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /**
   * Accept a shared proposal at the scope the client chose.
   *
   * Sends CHOICES, never a price. The figure comes back computed by the API
   * from the frozen document, because a total posted by a browser is a total
   * the browser could set.
   */
  async acceptShared(
    token: string,
    input: AcceptProposalInput,
  ): Promise<ProposalSelectionDto> {
    try {
      return await api.post(
        `/shared-proposals/${encodeURIComponent(token)}/accept`,
        input,
        proposalSelectionDtoSchema,
      )
    } catch (error) {
      logger.error("Failed to accept a shared proposal", {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async decide(
    versionId: string,
    input: ProposalVersionDecisionInput,
  ): Promise<ProposalVersionDto> {
    try {
      return await api.post(
        `/proposal-versions/${encodeURIComponent(versionId)}/decision`,
        input,
        proposalVersionDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to record a proposal decision', {
        versionId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }
}

export const proposalService = new ProposalService()

/**
 * The address of one photo on a shared proposal, for an `<img src>`.
 *
 * Not a method on the service, because nothing is fetched: the browser loads
 * it, the API answers with a short-lived signed redirect, and only photos on
 * the proposal this token opens are served.
 */
export function sharedPhotoUrl(token: string, documentId: string): string {
  return apiUrl(
    `/shared-proposals/${encodeURIComponent(token)}/photos/${encodeURIComponent(documentId)}`,
  )
}
